<script lang="ts" setup>
import { ref, onMounted, reactive, nextTick } from "vue";
import { Globals } from "@/Globals";
import { getSiteData } from "@/NavigationHelper";
import { getKennungValueDisplay, getKennungTitle } from "@/DataHelper";
import { showSnackbar } from "@/UIHelper";
import { globalsBegriffMemo, checkBegriff } from "@/local";
import {} from "@/CalcHelperStatic";
import { mountedBase, onSave, BaseSetup } from "@/DataLoaderStatic";
import AUApi from "@/api";
import { goBack } from "@/NavigationHelper";

import {
  createEditorValidator,
  provideEditorValidator,
} from "@/components/EditorValidator";
const validator = createEditorValidator();
provideEditorValidator(validator);
const emit = defineEmits<{
  back: [];
  calculated: [data?: any];
  "update:modelValue": [value: any];
}>();

let ort = ref<string>("");
let showKoordinaten = ref<boolean>(false);
let ankunftsKoordinaten = ref<string>("");
let addresseAnkunft = ref<any>();

const pageParams: DataPageParameter = {
  backOnSave: true,
  zeitbereich: AUApi.Current.zeitbereich,
  insertMode: false,
  query: "Web_RK_DREnde_Reisezeiten",
  emitBack: () => emit("back"),
};

const uiData = reactive<DataPageUIData>(BaseSetup(pageParams));

onMounted(async () => {
  uiData.isValueChangeTrackerDisabled = true;
  try {
    await mountedBase(uiData, pageParams, false);
    if (!uiData.showLoading && uiData.data) {
      const row = uiData.data[0];
      ort.value = getKennungValueDisplay(row, "CITY", uiData.columns);
      const ereignis = getKennungValueDisplay(
        row,
        "RK_REISEEREIGNISSE",
        uiData.columns,
      );
      showKoordinaten.value = getKennungTitle(
        "RK_ANKUNFTSKOORIDNATEN",
        uiData.columns,
      );
      if (!ereignis) {
        const meldung = "Reise wurde bereits beendet";
        showSnackbar({
          text: meldung,
          color: "warning",
        });
        emit("back");
      }
    } else {
      const meldung = "Reise wurde bereits beendet";
      showSnackbar({
        text: meldung,
        color: "warning",
      });
      emit("back");
    }
    uiData.initialized = true;
  } catch (error) {
    console.error(error);
  }
});
async function onValidate() {
  return await validator.validateAsync();
}
async function save() {
  const isvalid = await onValidate();
  if (!isvalid.valid) {
    showSnackbar({
      text:
        "Validierung fehlgeschlagen - Bitte prüfen! (" +
        isvalid.invalidFields.join(", ") +
        ")",
      duration: 4000,
      color: "error",
    });
    return;
  }
  let varUebergabe: VarUebergabe[] = [];
  const siteData = getSiteData();
  if (siteData.varUbergabe) {
    varUebergabe = siteData.varUbergabe;
  }
  if (ort.value || ankunftsKoordinaten.value) {
    const aicAbfAll = 0;

    onSave(
      uiData,
      pageParams,
      aicAbfAll,
      "",
      undefined,
      0,
      true,
      varUebergabe,
    )?.then(() => {
      nextTick(() => emit("calculated"));
    });
  } else {
    const ortText = getKennungTitle("CITY", uiData.columns);

    let meldung = globalsBegriffMemo(
      Globals.Begriff,
      "WEB_SpeichernNichtMoeglich",
      "Speichern kann nicht durchgeführt werden da folgende Daten leer sind",
    );
    meldung = meldung + ": " + ortText;
    showSnackbar({
      text: meldung,
      color: "warning",
    });
  }
}
function setValueAnkunft(res: any) {
  addresseAnkunft.value = res;
}
</script>
<template>
  <v-card class="pa-0" :loading="uiData.showLoading">
    <v-card-text v-if="uiData.initialized" height="300px">
      <span class="red--text" v-if="checkBegriff('Web_Zeitart_FortsetzungDR')">
        {{
          globalsBegriffMemo(
            Globals.Begriff,
            "Web_DienstreiseBeenden",
            "ACHTUNG: wenn es sich um eine MEHRTÄGIGE  Dienstreise handelt, verwenden Sie diese Funktion erst am LETZTEN Tag der Dienstreise. Möchten Sie eine Unterbrechnung der Dienstreise angeben, verwenden Sie die Funktion ‘Ende ausser Haus‘.",
          )
        }}
      </span>
      <v-row class="py-3 pl-3">
        <span style="white-space: nowrap"
          >{{ getKennungTitle("RK_REISEBEWSTAMM", uiData.columns) }}:
        </span>

        <span class="pl-2" style="white-space: nowrap">{{
          getKennungValueDisplay(
            uiData.data[0],
            "RK_REISEBEWSTAMM",
            uiData.columns,
          )
        }}</span>
      </v-row>
      <v-row class="pb-6 pl-3">
        <span style="white-space: nowrap"
          >{{ getKennungTitle("RK_REISEEREIGNISSE", uiData.columns) }}:
        </span>

        <span class="pl-2" style="white-space: nowrap">{{
          getKennungValueDisplay(
            uiData.data[0],
            "RK_REISEEREIGNISSE",
            uiData.columns,
          )
        }}</span>
      </v-row>
      <EditComboBox
        v-if="!showKoordinaten"
        :modelValue="ort"
        @update:modelValue="ort = $event"
        kennung="CITY"
        :queryZeitbereich="pageParams.zeitbereich"
        :loadFavorite="true"
        :clearable="true"
      />
      <EditGps
        v-if="showKoordinaten"
        :modelValue="ankunftsKoordinaten"
        @update:modelValue="ankunftsKoordinaten = $event"
        kennung="RK_ANKUNFTSKOORIDNATEN"
        :queryZeitbereich="pageParams.zeitbereich"
        :loadFavorite="true"
        :clearable="false"
        @change="setValueAnkunft"
        class="pt-5"
        :showMap="true"
      />
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <CommonBaseButtons
        :deleteVisible="false"
        :createVisible="false"
        :refreshVisible="false"
        :backVisible="false"
        :saveVisible="true"
        @back="goBack()"
        @save="save"
      ></CommonBaseButtons>
    </v-card-actions>
  </v-card>
</template>
<style scoped>
.row {
  padding: 0px;
}
.v-stepper__step {
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: horizontal;
  -webkit-box-direction: normal;
  -ms-flex-direction: row;
  flex-direction: row;
  padding-left: 5px;
  padding-right: 0px;
  padding-top: 10px;
  padding-bottom: 10px;
  position: relative;
}
.theme--light.v-stepper .v-stepper__step--active .v-stepper__label {
  text-shadow: 0px 0px 0px black;
}
</style>
